import { RecoverableTerminalErrorStatus } from '@afterpay/types'
import { DesignLanguage, Loader, useGetSelectedDesignLanguage } from '@afterpaytouch/core'
import { useTranslation } from '../../utils/convergence'
import React, { ReactElement, useEffect } from 'react'

import { Head, ModuleContainer, RecoverableTerminalError } from '../../../src/component'
import { Route } from '../../../src/config/router'
import { CheckoutError, CheckoutPage, TerminalErrorCode } from '../../../src/model'
import { useAppRouter, useCheckoutError, useIsRecoverableFlowPageReady, useRecoverableTerminalErrorsState } from '../../../src/state'
import { TrackPageViewed } from '../../../src/integrations/monitoring'

type Translate = ReturnType<typeof useTranslation>['t']

const getPageTitle = (error: string | undefined, t: Translate): string => {
  switch (error) {
    case TerminalErrorCode.AmountTooHigh:
      return t('recoverableTerminalError:amountTooHigh.head')
    case TerminalErrorCode.MaxActiveOrdersReached:
      return t('recoverableTerminalError:maxActiveOrdersReached.head')
    case TerminalErrorCode.MaxActiveOrdersReachedDetails:
      return t('recoverableTerminalError:maxActiveOrdersReached_details.head')
    default:
      return t('recoverableTerminalError:default.heading')
  }
}

const DEFAULT_CHECKOUT_ERROR: CheckoutError = {
  error: TerminalErrorCode.Default,
}

export const OhNoErrorPage: CheckoutPage = (): ReactElement => {
  const { t } = useTranslation()
  const checkoutError = useCheckoutError() ?? DEFAULT_CHECKOUT_ERROR
  const pageTitle = getPageTitle(checkoutError?.error, t)
  const { status } = useRecoverableTerminalErrorsState()
  const router = useAppRouter()
  const isPageReady = useIsRecoverableFlowPageReady()
  const designLanguage = useGetSelectedDesignLanguage()

  useEffect(() => {
    if (status === RecoverableTerminalErrorStatus.LackOfEligibleOrders) {
      router.replace(Route.TERMINAL_ERROR)
    }
  }, [status, router])

  const mapDesignLanguageToLoader: Record<DesignLanguage, ReactElement> = {
    [DesignLanguage.Bronte]: (
      <ModuleContainer padding={{ vertical: 'Large', horizontal: 'XLarge' }}>
        <Loader />
      </ModuleContainer>
    ),
    [DesignLanguage.Arcade]: <Loader alignCenter withHeader />,
  }

  return (
    <>
      <Head title={pageTitle} />
      {!isPageReady && mapDesignLanguageToLoader[designLanguage]}
      <RecoverableTerminalError {...checkoutError} />
      <TrackPageViewed eventProperties={{ limitExceeded: checkoutError?.errorCode }} />
    </>
  )
}
